import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "An important part of the process of creating a new website, blog, email, or other Internet service is registering the new DNS domain name. Registration inserts the new domain name into the global DNS so that everyone across the Internet can resolve it and make use of its services.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("You can create a new website or other Internet service without registering a domain name. For example, you could create a new blog and publish it through someone else's domain, such as "),
  /*#__PURE__*/_createElementVNode("code", null, "example.wordpress.org"),
  /*#__PURE__*/_createTextVNode(". However, you might instead want your very own domain name, perhaps at "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(". To make this happen, you will have to register "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" in the DNS.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", null, "When you register a domain name, your registrar adds it to the set of domain names on the Internet DNS. In reality, of course, it's a bit more complex. So let's see how it really works.", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/how-does-domain-name-registration-work/registering-a-domain.jpg",
    alt: "Registering a domain is like adding a book to the library",
    title: "Registering a domain is like adding a book to the library",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Registering a domain is like adding a book to the library. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h2", null, "Registrants, registries, registrars, and ICANN", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "There are four main players in the process of registering a new domain name:", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "The registrant"),
  /*#__PURE__*/_createElementVNode("li", null, "The registrar"),
  /*#__PURE__*/_createElementVNode("li", null, "The registry"),
  /*#__PURE__*/_createElementVNode("li", null, "ICANN")
], -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h3", { id: "the-registrant" }, "The registrant", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, "This may be you! The domain registrant is the person or company who would like to register a new domain name. This may be the owner of a small business, the IT department of a large corporation, or an individual person who wants to register a name for their personal blog.", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h3", { id: "the-registrar" }, "The registrar", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, "A domain registrar is a company that sells services to allow end users to purchase and manage their own domain names in the DNS. A registrar leases domain names and provides the services necessary to insert that domain name into the global DNS.", -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", null, "Domain names are leased on a yearly basis, not bought outright. If the lease is not renewed, the domain name will be removed from the DNS and will become available for someone else to lease.", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("h3", { id: "the-registry" }, "The registry", -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("As mentioned earlier, registrars are companies that sell domain name registration services. Domain registries, on the other hand, are databases of DNS names under each of the TLDs. Each registry is operated by a different organization. For example, VeriSign operates the registry for the "),
  /*#__PURE__*/_createElementVNode("code", null, "com"),
  /*#__PURE__*/_createTextVNode(" TLD.")
], -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "As the registrant for a new domain name, you will need to work only with a registrar. The registrar will work with the registry on your behalf.", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("h3", null, "ICANN", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("All this, at a very high level, is overseen by "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://www.icann.org/",
    rel: "noopener noreferrer"
  }, "ICANN"),
  /*#__PURE__*/_createTextVNode(" (Internet Corporation for Assigned Names and Numbers). ICANN is a non-profit organization with headquarters in California. Formed in 1998 by the US government, ICANN transitioned in 2017 to a global multi-stakeholder model. ICANN manages domain name registries and IP addresses to ensure the stability of the Internet and to foster competition and customer choice. ICANN also oversees operation of the Internet root zone. ICANN also grants accreditation to registrars.")
], -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "Accreditation by ICANN is a long and complex process. Each registrar pays a fee to apply, plus a yearly accreditation fee, and also a quarterly fee. These fees go towards the operation of ICANN itself. Some of the requirements include:", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "A solid business plan"),
  /*#__PURE__*/_createElementVNode("li", null, "Financial solvency"),
  /*#__PURE__*/_createElementVNode("li", null, "A secure and robust network architecture"),
  /*#__PURE__*/_createElementVNode("li", null, "A plan for good customer service"),
  /*#__PURE__*/_createElementVNode("li", null, "Processes to prevent domain name abuse")
], -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("code", null, "\".\"", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Originally, the TLDs included the single label domains we are all familiar with today such as "),
  /*#__PURE__*/_createElementVNode("code", null, "com"),
  /*#__PURE__*/_createTextVNode(", "),
  /*#__PURE__*/_createElementVNode("code", null, "edu"),
  /*#__PURE__*/_createTextVNode(", "),
  /*#__PURE__*/_createElementVNode("code", null, "mil"),
  /*#__PURE__*/_createTextVNode(", and others. Today, these TLDs are called Generic Top-level Domains (gTLDs) because they are associated with a theme rather than a nation.")
], -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("There are also over 300 Country Code Top-level Domains (ccTLDs). Each ccTLD is for a specific nation such as "),
  /*#__PURE__*/_createElementVNode("code", null, "uk"),
  /*#__PURE__*/_createTextVNode(", "),
  /*#__PURE__*/_createElementVNode("code", null, "jp"),
  /*#__PURE__*/_createTextVNode(", and "),
  /*#__PURE__*/_createElementVNode("code", null, "au"),
  /*#__PURE__*/_createTextVNode(". Some ccTLDs have become popular names for business because they are catchy. For example, the ccTLD "),
  /*#__PURE__*/_createElementVNode("code", null, "fm"),
  /*#__PURE__*/_createTextVNode(" is for the Federated States of Micronesia but is often used by FM radio stations and podcasts, and the ccTLD "),
  /*#__PURE__*/_createElementVNode("code", null, "io"),
  /*#__PURE__*/_createTextVNode(" is for the British Indian Ocean Territory but has become popular with technology companies and startups (because IO is the acronym for Input/Output).")
], -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("h2", null, "What happens when you register a domain name?", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "As a new domain's registrant, here are the steps you will need to follow and the events that will occur in the background:", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Select a registrar"),
  /*#__PURE__*/_createElementVNode("li", null, "Select a new domain name and create a zone"),
  /*#__PURE__*/_createElementVNode("li", null, "Claim the new domain name with the registrar"),
  /*#__PURE__*/_createElementVNode("li", null, "Registrar sends NS records to the registry")
], -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("h3", null, "Select a registrar", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, "A registrant may use any registrar they wish, but not all registrars support all registries. Some newer TLDs and some ccTLDs may operate with only a few registrars, or perhaps even only one registrar.", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "Enable strong MFA on your registrar account to protect your domains from hijacking.", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("h3", null, "Select a new domain name and create a zone", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("p", null, "You may have a domain name in mind already, or you may use search tools provided by your registrar to see what names are available.", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("code", null, "example.org", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("p", null, "It is important to set up the zone correctly so that the new domain will function properly. Some registrars will validate the zone before they will publish NS records in the appropriate registry to protect their customers from accidental misconfigurations. Also note that a zone that is not set up correctly may cause the domain to be flagged as a potential source of spam or suspicious activity.", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "Some registrars may allow you to claim a domain before creating the zone. Check with your registrar for the details of their exact process.", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("h3", null, "Claim the new domain name with the registrar", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, "Log onto the web portal of your registrar using your account and purchase the new domain name for at least one year. The cost may run anywhere from a few dollars up to several hundred dollars per year, depending on the registrar and the new domain's TLD. The registrar may perform validation that your DNS zone has been properly created before allowing you to complete this step.", -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, "Part of this process will be to set contact information for the domain. This is called Whois information after the name of the Internet directory where it is stored. ICANN requires that all domains have owner information including the name of a person or company, a physical mailing address, and other information. Your registrar will insert this into the registry on your behalf, but it will be hidden for privacy.", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("h3", null, "Registrar sends NS records to the registry", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, "If at some point in the future you change the NS records for the domain, you must change them with your registrar as well. This change will also take minutes to hours to propagate to the DNS servers authoritative for the TLD. NS record changes for a domain should be done carefully and incrementally, with consideration for the TTLs of the records and the propagation time for the registrar and TLD.", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("This is a good kind of hacking. \"Domain hacking\" is coming up with a clever domain name that incorporates the TLD as part of a word or phrase. For example, "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://ta.co",
    rel: "noopener noreferrer",
    target: "_blank"
  }, "ta.co"),
  /*#__PURE__*/_createTextVNode(" redirects to Taco Bell's main website and "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://n.pr",
    rel: "noopener noreferrer",
    target: "_blank"
  }, "n.pr"),
  /*#__PURE__*/_createTextVNode(" redirects to National Public Radio's (NPR) main website. These domain hacks use the ccTLDs of Colombia and Puerto Rico, even though neither is tied to those locales.")
], -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, "Different ccTLDs have different restrictions on who may register domains with them. Some welcome registrants from across the globe, but other ccTLDs require registrants to have ties to the country or territory of the ccTLD.", -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("h2", null, "Changing an existing domain", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("p", null, "After registering the domain, there are two major types of changes that might be made to DNS records for the domain:", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Changes to DNS records within the zone"),
  /*#__PURE__*/_createElementVNode("li", null, "Changes to the zone's NS records")
], -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("If "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" has been registered, then at any time the DNS administrator may add or change DNS records inside the zone. For example, to add or update records at "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(". This includes all records other than type NS at "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" itself. This type of change can be made in the zone at any time. No changes by the registrar are necessary, and it is not necessary to inform the registrar that changes in the zone are being made.")
], -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("p", null, "There are a number of strategies that can be employed to safely change NS records. These may involve making a single NS record change at a time, with a pause between stages to ensure that nothing has gone wrong. Good monitoring and a plan to roll the changes back if something goes wrong are strongly recommended.", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("h2", null, "Domain transfer", -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("p", null, "ICANN supports transferring domains from one registrar to another, and transferring ownership from one party to another as part of its mandates to promote competition and customer choice.", -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("p", null, "Transferring ownership of an existing domain replaces the current owner of the domain in the registry with a new owner. Each registrar has their own process to ensure that the request is legitimate. Generally, both parties must agree to the transfer via a secure mechanism.", -1)
const _hoisted_49 = /*#__PURE__*/_createElementVNode("p", null, "The owner of a domain may also transfer the domain to a new registrar. You might transfer to a new registrar if you are unhappy with the service provided by your current registrar.", -1)
const _hoisted_50 = /*#__PURE__*/_createElementVNode("p", null, "There are some restrictions on this. For example, a domain transfer request may be denied if the domain is less than 60 days old to prevent abuse by malicious parties.", -1)
const _hoisted_51 = /*#__PURE__*/_createElementVNode("h2", null, "Domain locking", -1)
const _hoisted_52 = /*#__PURE__*/_createElementVNode("p", null, "Many registrars offer the ability to \"lock\" the domain to the registrar due to incidents of malicious domain hijacking in the past. The registrar will automatically refuse any request to transfer the domain to another registrar while a domain is locked.", -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, "Domain locking is a good safeguard against domain hijacking, where a malicious party attempts to fraudulently transfer the domain to another registrar, so they can take it over. The domain can be unlocked at any time. This requires an additional factor of authentication.", -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("h2", null, "Keeping domains safe", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("p", null, "It's important to keep your domains safe. An attacker may attempt to hijack your domains through social engineering or other means, to steal data from your customers or to use your domain for some other nefarious purpose.", -1)
const _hoisted_56 = /*#__PURE__*/_createElementVNode("p", null, "There are a few things that DNS administrators can and should do to keep domains safe:", -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Strong passwords"),
    /*#__PURE__*/_createTextVNode(": Use unique and strong passwords for registrar accounts.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Use multi-factor authentication (MFA)"),
    /*#__PURE__*/_createTextVNode(": Enable strong MFA on registrar accounts. Note that MFA based on text (SMS) is not particularly strong.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Use domain locking"),
    /*#__PURE__*/_createTextVNode(": Domain transfer is rare, so keep domains locked to the registrar whenever possible.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Don't allow domains to expire"),
    /*#__PURE__*/_createTextVNode(": Once the lease expires, another party may be able to register the domain. Use auto-renewal for payment and update payment methods when necessary, for example when a new credit card number is issued. You may want to pre-pay for a number of years if the registrar permits.")
  ])
], -1)
const _hoisted_58 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_59 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_60 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_61 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How to select a registrar? ", -1)
const _hoisted_62 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_63 = /*#__PURE__*/_createElementVNode("p", null, "There are many registrars out there these days, all offering similar services. There are a few things you might consider when selecting a registrar:", -1)
const _hoisted_64 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Security"),
    /*#__PURE__*/_createTextVNode(": Many domains have been hijacked in the past, often through social engineering and sloppy security practices by registrars. It is worth selecting a registrar that offers strong multi-factor authentication (MFA) and is known for a commitment to security.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Reputation"),
    /*#__PURE__*/_createTextVNode(": It may be tempting to select a small registrar to save a few dollars. The security of your domain is likely very important to you, so it may be worth selecting a well-known, large registrar that has a good reputation for security and customer service.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "Ecosystem"),
    /*#__PURE__*/_createTextVNode(": You may already host your website or service on a platform that also offers domain registration. It may be easiest to purchase all of your services from a single vendor. This will most likely make administration tasks simpler.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "SSL support"),
    /*#__PURE__*/_createTextVNode(": If you are creating a new website, you may want to also purchase SSL certificates for your new domain name, so selecting a registrar that offers this service may be important.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createElementVNode("strong", null, "TLD support"),
    /*#__PURE__*/_createTextVNode(": Not all registrars can lease names in all TLDs. If you have a particular TLD in mind, for example the ccTLD of your home nation, you may need to select a registrar that can operate with that TLD.")
  ])
], -1)
const _hoisted_65 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " How to select a domain name? ", -1)
const _hoisted_66 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_67 = /*#__PURE__*/_createElementVNode("p", null, "Finding the right domain name for your new website or service can be challenging.", -1)
const _hoisted_68 = /*#__PURE__*/_createElementVNode("p", null, "Most registrars offer search tools to help you find a domain name. If you want your domain name to include a particular word, these search tools can give you a list of variants that are available and show you which TLDs do not already have that term registered.", -1)
const _hoisted_69 = /*#__PURE__*/_createElementVNode("p", null, "Domain names should be short and easy for people to remember. It is generally best to avoid numbers in domain names, but they are legal. It is advisable to avoid trademarks and confusion with other businesses.", -1)
const _hoisted_70 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Part of selecting a domain name will be to select a TLD. It's best to choose a TLD that will be easy for customers to remember. The "),
  /*#__PURE__*/_createElementVNode("code", null, "com"),
  /*#__PURE__*/_createTextVNode(" TLD is always a good choice for a business website, or you may choose a TLD that is commonly used by other businesses in your industry. If your business is strongly tied to a particular country, then the ccTLD for your country may be a good choice.")
], -1)

import BlogPost from '@/components/layout/BlogPost.vue';

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';

const meta: BlogMeta = {
  tag: 'Domain names',
  url: '/learning/how-does-domain-name-registration-work/',
  breadcrumbs: [learningBreadcrumb],
  title: 'How does domain name registration work?',
  description: 'Domain registration is the process of making a new DNS zone available on the Internet.',
  overviewDescription: 'Domain registration is the process of making a new DNS zone available on the Internet.',
  overviewImage: '/img/learning/how-does-domain-name-registration-work/registering-a-domain-small.jpg',
  overviewImageAlt: 'How does domain name registration work?',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/how-does-domain-name-registration-work/how-does-domain-name-registration-work-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/how-does-domain-name-registration-work/how-does-domain-name-registration-work-social.jpg',
  written: new Date('2022-06-09T02:34:25.045Z'),
  updated: new Date('2023-03-28T12:00:07.611Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'HowDoesDomainNameRegistrationWork',
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/how-does-domain-name-registration-work/registering-a-domain.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _hoisted_12,
        _hoisted_13,
        _hoisted_14,
        _createElementVNode("p", null, [
          _createTextVNode("The registrant is the customer who wishes to register a new domain name. The registrant engages a registrar to create and manage registration of a domain name. And finally, the registrar works with the registry to actually create the "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/ns/" }, {
            default: _withCtx(() => [
              _createTextVNode("NS records")
            ]),
            _: 1
          }),
          _createTextVNode(" in the TLD for the new domain name.")
        ]),
        _hoisted_15,
        _hoisted_16,
        _hoisted_17,
        _hoisted_18,
        _hoisted_19,
        _createVNode(Callout, {
          icon: "info",
          title: "Top-level domains",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _createElementVNode("p", null, [
              _createTextVNode("The DNS begins at the root zone "),
              _hoisted_20,
              _createTextVNode(" and flows downwards into the top-level domains (TLDs). As we discussed in our article on "),
              _createVNode(_component_router_link, {
                to: "/learning/zone-delegation/",
                title: "DNS zone delegation"
              }, {
                default: _withCtx(() => [
                  _createTextVNode("zone delegation")
                ]),
                _: 1
              }),
              _createTextVNode(", the DNS resolves names starting at the root and moving downwards through delegations. The root zone and the TLDs are involved in resolving every single DNS name on the Internet!")
            ]),
            _hoisted_21,
            _hoisted_22
          ]),
          _: 1
        }),
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _createElementVNode("p", null, [
          _createTextVNode("Once you have "),
          _createVNode(_component_router_link, { to: "/learning/10-tips-for-picking-the-perfect-domain-name/" }, {
            default: _withCtx(() => [
              _createTextVNode("selected your new domain name")
            ]),
            _: 1
          }),
          _createTextVNode(", you must create a "),
          _createVNode(_component_router_link, {
            to: "/learning/what-is-a-dns-zone/",
            title: "DNS zone"
          }, {
            default: _withCtx(() => [
              _createTextVNode("DNS zone")
            ]),
            _: 1
          }),
          _createTextVNode(" for it. Your registrar may offer this as a service. Or you might prefer to create and manage the DNS zone yourself using a DNS hosting provider, or even on your own DNS servers.")
        ]),
        _createElementVNode("p", null, [
          _createTextVNode("If you are going to register "),
          _hoisted_31,
          _createTextVNode(" then you or someone acting on your behalf must create that zone somewhere. There must be an "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/soa/" }, {
            default: _withCtx(() => [
              _createTextVNode("SOA record")
            ]),
            _: 1
          }),
          _createTextVNode(" at the root of the zone, which must contain an administrative email address for the zone in the Responsible Person field and the host name of a DNS server "),
          _createVNode(_component_router_link, { to: "/learning/recursive-vs-authoritative-dns/" }, {
            default: _withCtx(() => [
              _createTextVNode("authoritative")
            ]),
            _: 1
          }),
          _createTextVNode(" for the zone in the Primary Name Server field. The zone must also have at least two "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/ns/" }, {
            default: _withCtx(() => [
              _createTextVNode("NS records")
            ]),
            _: 1
          }),
          _createTextVNode(" at the root of the zone. Each NS record must list a DNS server authoritative for the zone.")
        ]),
        _hoisted_32,
        _hoisted_33,
        _hoisted_34,
        _hoisted_35,
        _hoisted_36,
        _hoisted_37,
        _createElementVNode("p", null, [
          _createTextVNode("Once you have submitted the domain registration request to the registrar and paid for one or more years of lease, your work is complete. The registrar will communicate your request along with the "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/ns/" }, {
            default: _withCtx(() => [
              _createTextVNode("NS records")
            ]),
            _: 1
          }),
          _createTextVNode(" for the new domain to the registry for the TLD. The changes will propagate through the network infrastructure of the registry and become visible to the DNS on the Internet. This process may take as little as 20 minutes up to several hours, or longer depending on the registrar and the registry. ")
        ]),
        _hoisted_38,
        _createVNode(Callout, {
          icon: "info",
          title: "Domain hacking",
          heading: "h3"
        }, {
          default: _withCtx(() => [
            _hoisted_39,
            _hoisted_40
          ]),
          _: 1
        }),
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _hoisted_44,
        _createElementVNode("p", null, [
          _createTextVNode("If the NS records at the root of the zone are changed, however, this must be communicated with the registrar. This type of change can be very tricky and if not done correctly can result in a "),
          _createVNode(_component_router_link, {
            to: "/learning/zone-delegation/#lame-delegations",
            title: "Lame delegations"
          }, {
            default: _withCtx(() => [
              _createTextVNode("lame delegation")
            ]),
            _: 1
          }),
          _createTextVNode(" and outage for the domain.")
        ]),
        _hoisted_45,
        _hoisted_46,
        _hoisted_47,
        _hoisted_48,
        _hoisted_49,
        _hoisted_50,
        _hoisted_51,
        _hoisted_52,
        _hoisted_53,
        _hoisted_54,
        _hoisted_55,
        _hoisted_56,
        _hoisted_57,
        _createElementVNode("div", _hoisted_58, [
          _hoisted_59,
          _createElementVNode("div", _hoisted_60, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_61,
                    _createElementVNode("span", _hoisted_62, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_63,
                    _hoisted_64
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_65,
                    _createElementVNode("span", _hoisted_66, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_67,
                    _hoisted_68,
                    _hoisted_69,
                    _hoisted_70
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})